import { useState, useEffect } from 'react';
import './PatientCallerWidget.scss';
import { getDate } from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { getQueueNumbersSortedByPA, deactivateQueueNumber, callPatient, getHistoricQueueNumbers } from '../../../services/PatientCallerController';
import { QueueNumberDTO } from '../../../interface/interface';
import { connectToQueueNumberQueue, fetchSiteInfo } from '../../../services/PatientService';
import { formatDateTime, ETypeOfNotification, setNotification } from '@medere/medere-ui-framework';
import { useDispatch } from 'react-redux';
import { getHospitalLocationIdByHardwareId } from '../../../services/LocationConfigController';

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export default function PatientCallerWidget({ open, setOpen }: Props) {
  const [waitingList, setWaitingList] = useState<QueueNumberDTO[]>([]);
  const [attendedList, setAttendedList] = useState<QueueNumberDTO[]>([]);
  const [activeTab, setActiveTab] = useState<'waiting' | 'attended'>('waiting');
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<QueueNumberDTO | null>(null);
  const [queueList, setQueueList] = useState<QueueNumberDTO[]>([]);
  const dispatch = useDispatch();

  const [siteUuid, setSiteUuid] = useState<string>('');
  const [hardwareHospitalLocationId, setHardwareHospitalLocationId] = useState<number>(-1);

  useEffect(() => {
    if (!siteUuid || hardwareHospitalLocationId <= 0) return; //si hardwareHospitalLocationId <= 0, PatientCallerWidget no aparece y no hace falta cargar datos

    const refreshQueues = () =>
      getQueueNumbersSortedByPA(localStorage.getItem("uuid") || '').then(setQueueList);

    const handleCalled = (qn: QueueNumberDTO) => {
      setQueueList(prev => prev.filter(x => x.queuenumber !== qn.queuenumber));
      setAttendedList(prev => [qn, ...prev]);
    };

    const handleDeactivated = (qn: QueueNumberDTO) =>
      setQueueList(prev => prev.filter(x => x.queuenumber !== qn.queuenumber));

    connectToQueueNumberQueue(siteUuid, hardwareHospitalLocationId, refreshQueues, handleCalled, handleDeactivated);
  }, [siteUuid, hardwareHospitalLocationId]);

  useEffect(() => {
    const loadSiteInfo = async () => {
      try {
        const siteInfo = await fetchSiteInfo();
        setSiteUuid(siteInfo.siteuuid);
      } catch (err) {
        console.error('Error cargando la info del sitio:', err);
      }
    };
    loadSiteInfo();

    const loadAssociatedHospitalLocationId = async () => {
      try {
        const hlId = await getHospitalLocationIdByHardwareId();
        setHardwareHospitalLocationId(hlId ?? -1);
      } catch (err) {
        console.error('Error cargando la info del sitio:', err);
      }
    };
    loadAssociatedHospitalLocationId();

    getQueueNumbersSortedByPA(localStorage.getItem("uuid") || '')
      .then(data => {
        setQueueList(data);
      })
      .catch(() => console.warn('No se pudo obtener cola desde backend'));

      getHistoricQueueNumbers()
      .then(data => {
        setAttendedList(data);
      })
      .catch(() => console.warn('No se pudo obtener cola desde backend'));
  }, []);

  const callNext = () => {
    const next = queueList.find(q => !q.callingdate);
    if (next) callSpecific(next);
  };

  const callSpecific = async (item: QueueNumberDTO) => {
    try {
      const qn = await callPatient(item, localStorage.getItem('uuid') || '');
      
      const now = new Date();
      
      setQueueList(p => p.filter(q => q.queuenumber !== item.queuenumber));
      
      setAttendedList(p => [{ ...item, callingdate: now, calleruser: qn.calleruser, calleruserdesc: qn.calleruserdesc }, ...p]);

    } catch (err: any) {
      if (err?.response?.status === 409) {
        dispatch(setNotification({title: 'No se puede llamar', desc: 'Para llamar se requiere asociado tu puesto de atención a algún llamador', icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING}));
      } else {
        console.error(err);
        dispatch(setNotification({title: 'Error', desc: 'No se pudo llamar al paciente', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR}));
      }
    }
  };

  const deleteItem = (id: number) => {
    deactivateQueueNumber(id)
    .then(() => {
      setQueueList(prev => prev.filter(i => i.queuenumber !== id));
    })
    .catch(() => console.warn("No se pudo desactivar en backend"));
  };


  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return isNaN(date.getTime())
      ? timestamp
      : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, });
  };

  const getWaitingTime = (start: string, end: string) => {
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);

      const diffMs = endDate.getTime() - startDate.getTime();
      if (diffMs < 0 || isNaN(diffMs)) return '-';

      const totalSeconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${pad(minutes)}:${pad(seconds)}`;
    } catch {
      return '-';
    }
  };

  const renderList = (list: QueueNumberDTO[]) => {
    const isAttended = activeTab === 'attended';

    return (
      <div className="patient-list">
        {list.map(item => (
          <div
            key={item.queuenumber}
            className="patient-item"
            style={isAttended ? { alignItems: 'flex-start', minHeight: 'unset' } : undefined}
          >
            <div
              className="code"
              style={isAttended ? { height: 'auto', minHeight: 35, alignSelf: 'flex-start' } : undefined}
            >
              {`${item.queuestringid}${String(item.number).padStart(3, '0')}`}
            </div>

            <div className="info">
              <div
                className="area -mb-1"
                style={isAttended ? { whiteSpace: 'normal', wordBreak: 'break-word' } : undefined}
              >
                {item.optiondescription}
              </div>

              <div
                className="time flex flex-col"
                style={isAttended ? { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' } : undefined}
              >
                <div className="time flex flex-row">
                  <p className={`text-[${activeTab === 'waiting' ? '10px' : '11px'}] mr-0.5`}>
                    Llegada:{' '}
                    {activeTab === 'waiting'
                      ? formatDateTime(getDate(item.recorddate).toISOString())
                      : formatTime(getDate(item.recorddate).toISOString())}
                  </p>
                  {item.callingdate && (
                    <>
                      {' '}-{' '}
                      <p className={`text-[${activeTab === 'waiting' ? '10px' : '11px'}] ml-0.5`}>
                        Espera:{' '}
                        {getWaitingTime(item.recorddate!.toString(), item.callingdate.toString())}
                      </p>
                    </>
                  )}
                </div>

                {isAttended && (
                  <div>
                    <p className="text-[11px] mr-0.5" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      Atendido por: {item.calleruserdesc}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {activeTab === 'waiting' && (
              <div className="icons" style={{ marginLeft: 'auto' }}>
                <div
                  className="icon-button"
                  onClick={() => { setItemToRemove(item); setOpenRemoveModal(true); }}
                >
                  <i className="pi pi-trash" />
                </div>
                <div className="icon-button" onClick={() => callSpecific(item)}>
                  <i className="pi pi-megaphone" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`floating-caller-container ${open ? 'open' : ''}`}>
      <button className="toggle-btn" onClick={() => setOpen(!open)}>
        <div className="icon">
          <i className="pi pi-megaphone" />
        </div>
      </button>

      <div className="caller-panel">
        <div className="panel-header">
          <h3>Llamador de pacientes</h3>
          <button className="next-btn" onClick={callNext}>Llamar siguiente</button>
        </div>

        <div className="panel-tabs">
          <button
            className={activeTab === 'waiting' ? 'active-tab' : ''}
            onClick={() => setActiveTab('waiting')}
          >
            En espera ({queueList.filter(i => i.recorddate).length})
          </button>
          <button
            className={activeTab === 'attended' ? 'active-tab' : ''}
            onClick={() => setActiveTab('attended')}
          >
            Llamados
          </button>
        </div>

        {openRemoveModal && (
          <>
            <div className="overlay-blocker" onClick={() => {
              setOpenRemoveModal(false);
              setItemToRemove(null);
            }} />
            <div className="local-modal">
              <div className="local-modal-content">
                <p className="modal-title">¿Estás seguro de eliminar?</p>
                <p className="modal-message">Esta acción no se puede deshacer.</p>
                <div className="modal-buttons">
                  <Button label="Sí, eliminar" onClick={() => {
                    if (itemToRemove) deleteItem(itemToRemove.queuenumber);
                    setOpenRemoveModal(false);
                    setItemToRemove(null);
                  }} className="w-full buttonDelete" />
                  <Button label="Cancelar" onClick={() => {
                    setOpenRemoveModal(false);
                    setItemToRemove(null);
                  }} className="w-full btnCancelDelete" />
                </div>
              </div>
            </div>
          </>
        )}

        {renderList(activeTab === 'waiting' ? queueList.filter(i => !i.callingdate) : attendedList)}
      </div>
    </div>
  );
}
